import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import {
  getDurableStateStorage,
  registerDurableRehydrator,
  WORKFLOW_RESUME_STORE_STORAGE_KEY,
} from '../lib/durableState/durableState';
import { DEFAULT_PIPEEX_PROFILE_NAME } from '../config/defaultPipeexProfile/defaultPipeexProfile';
import { normalizeMappingProjectKey } from '../lib/mappingProjectIdentity';

export type MappingWorkspaceResumeTab = 'mapping' | 'quickFill' | 'validation' | 'export';
export type MappingWorkspaceLandingMode = 'summary' | 'workspace';
export type ValidationResumeSurface = 'queue' | 'repairDetail' | 'conditionStudio';
export type ValidationResumeSeverityFilter = 'all' | 'critical' | 'error' | 'warning' | 'info';
export type ValidationResumeBlockerFilter = 'all' | 'blocking';
export type ValidationResumeCategory =
  | 'all'
  | 'mapping'
  | 'required-export'
  | 'connectors'
  | 'properties'
  | 'components';
export type ValidationResumeRepairTab = 'recommended' | 'surgery' | 'history';
export type ValidationResumeConditionTargetKey =
  | 'required-export'
  | 'connectors'
  | 'mapping'
  | 'classification'
  | 'properties';

export interface MappingWorkspaceResumeNode {
  type: string;
  name?: string;
}

export interface MappingWorkspaceResume {
  activeTab: MappingWorkspaceResumeTab;
  selectedNode: MappingWorkspaceResumeNode;
  landingMode: MappingWorkspaceLandingMode;
}

export interface ValidationWorkspaceRepairDetailResume {
  familyId: string;
  selectedItemId: string | null;
  activeTab: ValidationResumeRepairTab;
  surgeryMode: boolean;
}

export interface ValidationWorkspaceConditionStudioResume {
  targetKey: ValidationResumeConditionTargetKey;
  selectedConditionSetId: string | null;
  selectedValidationId?: number | null;
  sourceFamilyId: string | null;
}

export interface ValidationWorkspaceResume {
  selectedFamilyId: string | null;
  searchQuery: string;
  severityFilter: ValidationResumeSeverityFilter;
  blockerFilter: ValidationResumeBlockerFilter;
  requiredOnly: boolean;
  categoryFilter: ValidationResumeCategory;
  showPassedOnly: boolean;
  passedCollapsed: boolean;
  surface: ValidationResumeSurface;
  repairDetail: ValidationWorkspaceRepairDetailResume | null;
  conditionStudio: ValidationWorkspaceConditionStudioResume | null;
}

interface WorkflowResumeState {
  mappingByScope: Record<string, MappingWorkspaceResume>;
  validationByScope: Record<string, ValidationWorkspaceResume>;
  setMappingResume: (
    scopeKey: string,
    updater:
      | MappingWorkspaceResume
      | ((current: MappingWorkspaceResume) => MappingWorkspaceResume)
  ) => void;
  setValidationResume: (
    scopeKey: string,
    updater:
      | ValidationWorkspaceResume
      | ((current: ValidationWorkspaceResume) => ValidationWorkspaceResume)
  ) => void;
  clearScopeResume: (scopeKey: string) => void;
  reset: () => void;
}

const STORAGE_NAME = WORKFLOW_RESUME_STORE_STORAGE_KEY;
const LEGACY_USER_DEFINED_PROFILE_NAME = 'User Defined';
const LEGACY_USER_DEFINED_PROFILE_KEY_PREFIX = 'User Defined::';

export function createDefaultMappingWorkspaceResume(): MappingWorkspaceResume {
  return {
    activeTab: 'mapping',
    selectedNode: {
      type: 'objectType',
      name: 'ALL',
    },
    landingMode: 'summary',
  };
}

export function createDefaultValidationWorkspaceResume(): ValidationWorkspaceResume {
  return {
    selectedFamilyId: null,
    searchQuery: '',
    severityFilter: 'all',
    blockerFilter: 'all',
    requiredOnly: false,
    categoryFilter: 'all',
    showPassedOnly: false,
    passedCollapsed: true,
    surface: 'queue',
    repairDetail: null,
    conditionStudio: null,
  };
}

export function buildWorkflowResumeScopeKey(
  selectedSettingsType: number | string,
  selectedProfileKey: string | null | undefined,
  activeProjectKey?: string | null
): string {
  const normalizedSettingsType = String(selectedSettingsType ?? 'user').trim() || 'user';
  let normalizedProfileKey =
    typeof selectedProfileKey === 'string' && selectedProfileKey.trim() !== ''
      ? selectedProfileKey.trim()
      : 'unscoped';

  if (
    (normalizedSettingsType === '1' || normalizedSettingsType.toLowerCase() === 'user') &&
    normalizedProfileKey.startsWith(LEGACY_USER_DEFINED_PROFILE_KEY_PREFIX)
  ) {
    normalizedProfileKey = normalizedProfileKey.replace(
      LEGACY_USER_DEFINED_PROFILE_NAME,
      DEFAULT_PIPEEX_PROFILE_NAME
    );
  }

  if (typeof activeProjectKey === 'undefined') {
    return `${normalizedSettingsType}::${normalizedProfileKey}`;
  }

  const normalizedProjectKey = normalizeMappingProjectKey(activeProjectKey) ?? 'no-project';

  return `${normalizedSettingsType}::${normalizedProjectKey}::${normalizedProfileKey}`;
}

const initialState = {
  mappingByScope: {},
  validationByScope: {},
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isEqualValue = (left: unknown, right: unknown): boolean =>
  JSON.stringify(left) === JSON.stringify(right);

const MAPPING_TABS = new Set<MappingWorkspaceResumeTab>([
  'mapping',
  'quickFill',
  'validation',
  'export',
]);
const LANDING_MODES = new Set<MappingWorkspaceLandingMode>(['summary', 'workspace']);
const VALIDATION_SURFACES = new Set<ValidationResumeSurface>([
  'queue',
  'repairDetail',
  'conditionStudio',
]);
const VALIDATION_SEVERITIES = new Set<ValidationResumeSeverityFilter>([
  'all',
  'critical',
  'error',
  'warning',
  'info',
]);
const VALIDATION_BLOCKERS = new Set<ValidationResumeBlockerFilter>(['all', 'blocking']);
const VALIDATION_CATEGORIES = new Set<ValidationResumeCategory>([
  'all',
  'mapping',
  'required-export',
  'connectors',
  'properties',
  'components',
]);
const VALIDATION_REPAIR_TABS = new Set<ValidationResumeRepairTab>([
  'recommended',
  'surgery',
  'history',
]);
const VALIDATION_TARGET_KEYS = new Set<ValidationResumeConditionTargetKey>([
  'required-export',
  'connectors',
  'mapping',
  'classification',
  'properties',
]);

function sanitizeMappingWorkspaceResume(value: unknown): MappingWorkspaceResume {
  const defaults = createDefaultMappingWorkspaceResume();
  if (!isRecord(value)) {
    return defaults;
  }

  const selectedNodeValue = isRecord(value.selectedNode) ? value.selectedNode : {};

  return {
    activeTab: MAPPING_TABS.has(value.activeTab as MappingWorkspaceResumeTab)
      ? (value.activeTab as MappingWorkspaceResumeTab)
      : defaults.activeTab,
    selectedNode: {
      type:
        typeof selectedNodeValue.type === 'string' && selectedNodeValue.type.trim() !== ''
          ? selectedNodeValue.type
          : defaults.selectedNode.type,
      ...(typeof selectedNodeValue.name === 'string' && selectedNodeValue.name.trim() !== ''
        ? { name: selectedNodeValue.name }
        : defaults.selectedNode.name
          ? { name: defaults.selectedNode.name }
          : {}),
    },
    landingMode: LANDING_MODES.has(value.landingMode as MappingWorkspaceLandingMode)
      ? (value.landingMode as MappingWorkspaceLandingMode)
      : defaults.landingMode,
  };
}

function sanitizeValidationRepairDetailResume(
  value: unknown
): ValidationWorkspaceRepairDetailResume | null {
  if (!isRecord(value) || typeof value.familyId !== 'string' || value.familyId.trim() === '') {
    return null;
  }

  return {
    familyId: value.familyId,
    selectedItemId:
      typeof value.selectedItemId === 'string' && value.selectedItemId.trim() !== ''
        ? value.selectedItemId
        : null,
    activeTab: VALIDATION_REPAIR_TABS.has(value.activeTab as ValidationResumeRepairTab)
      ? (value.activeTab as ValidationResumeRepairTab)
      : 'recommended',
    surgeryMode: value.surgeryMode === true,
  };
}

function sanitizeValidationConditionStudioResume(
  value: unknown
): ValidationWorkspaceConditionStudioResume | null {
  if (!isRecord(value) || !VALIDATION_TARGET_KEYS.has(value.targetKey as ValidationResumeConditionTargetKey)) {
    return null;
  }

  return {
    targetKey: value.targetKey as ValidationResumeConditionTargetKey,
    selectedConditionSetId:
      typeof value.selectedConditionSetId === 'string' && value.selectedConditionSetId.trim() !== ''
        ? value.selectedConditionSetId
        : null,
    selectedValidationId:
      typeof value.selectedValidationId === 'number' && Number.isFinite(value.selectedValidationId)
        ? value.selectedValidationId
        : null,
    sourceFamilyId:
      typeof value.sourceFamilyId === 'string' && value.sourceFamilyId.trim() !== ''
        ? value.sourceFamilyId
        : null,
  };
}

function sanitizeValidationWorkspaceResume(value: unknown): ValidationWorkspaceResume {
  const defaults = createDefaultValidationWorkspaceResume();
  if (!isRecord(value)) {
    return defaults;
  }

  return {
    selectedFamilyId:
      typeof value.selectedFamilyId === 'string' && value.selectedFamilyId.trim() !== ''
        ? value.selectedFamilyId
        : null,
    searchQuery: typeof value.searchQuery === 'string' ? value.searchQuery : defaults.searchQuery,
    severityFilter: VALIDATION_SEVERITIES.has(value.severityFilter as ValidationResumeSeverityFilter)
      ? (value.severityFilter as ValidationResumeSeverityFilter)
      : defaults.severityFilter,
    blockerFilter: VALIDATION_BLOCKERS.has(value.blockerFilter as ValidationResumeBlockerFilter)
      ? (value.blockerFilter as ValidationResumeBlockerFilter)
      : defaults.blockerFilter,
    requiredOnly: value.requiredOnly === true,
    categoryFilter: VALIDATION_CATEGORIES.has(value.categoryFilter as ValidationResumeCategory)
      ? (value.categoryFilter as ValidationResumeCategory)
      : defaults.categoryFilter,
    showPassedOnly: value.showPassedOnly === true,
    passedCollapsed: value.passedCollapsed !== false,
    surface: VALIDATION_SURFACES.has(value.surface as ValidationResumeSurface)
      ? (value.surface as ValidationResumeSurface)
      : defaults.surface,
    repairDetail: sanitizeValidationRepairDetailResume(value.repairDetail),
    conditionStudio: sanitizeValidationConditionStudioResume(value.conditionStudio),
  };
}

function sanitizeMappingByScope(value: unknown): Record<string, MappingWorkspaceResume> {
  if (!isRecord(value)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(value).map(([scopeKey, resume]) => [scopeKey, sanitizeMappingWorkspaceResume(resume)])
  );
}

function sanitizeValidationByScope(value: unknown): Record<string, ValidationWorkspaceResume> {
  if (!isRecord(value)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(value).map(([scopeKey, resume]) => [scopeKey, sanitizeValidationWorkspaceResume(resume)])
  );
}

export const useWorkflowResumeStore = create<WorkflowResumeState>()(
  persist(
    (set) => ({
      ...initialState,

      setMappingResume: (scopeKey, updater) => {
        if (!scopeKey) {
          return;
        }

        set((state) => {
          const current = state.mappingByScope[scopeKey] ?? createDefaultMappingWorkspaceResume();
          const next =
            typeof updater === 'function'
              ? (updater as (current: MappingWorkspaceResume) => MappingWorkspaceResume)(current)
              : updater;
          const sanitizedNext = sanitizeMappingWorkspaceResume(next);

          if (isEqualValue(current, sanitizedNext)) {
            return state;
          }

          return {
            mappingByScope: {
              ...state.mappingByScope,
              [scopeKey]: sanitizedNext,
            },
          };
        });
      },

      setValidationResume: (scopeKey, updater) => {
        if (!scopeKey) {
          return;
        }

        set((state) => {
          const current =
            state.validationByScope[scopeKey] ?? createDefaultValidationWorkspaceResume();
          const next =
            typeof updater === 'function'
              ? (updater as (current: ValidationWorkspaceResume) => ValidationWorkspaceResume)(
                  current
                )
              : updater;
          const sanitizedNext = sanitizeValidationWorkspaceResume(next);

          if (isEqualValue(current, sanitizedNext)) {
            return state;
          }

          return {
            validationByScope: {
              ...state.validationByScope,
              [scopeKey]: sanitizedNext,
            },
          };
        });
      },

      clearScopeResume: (scopeKey) => {
        if (!scopeKey) {
          return;
        }

        set((state) => {
          const nextMappingByScope = { ...state.mappingByScope };
          const nextValidationByScope = { ...state.validationByScope };
          delete nextMappingByScope[scopeKey];
          delete nextValidationByScope[scopeKey];

          if (
            isEqualValue(nextMappingByScope, state.mappingByScope) &&
            isEqualValue(nextValidationByScope, state.validationByScope)
          ) {
            return state;
          }

          return {
            mappingByScope: nextMappingByScope,
            validationByScope: nextValidationByScope,
          };
        });
      },

      reset: () => {
        set(initialState);
      },
    }),
    {
      name: STORAGE_NAME,
      storage: createJSONStorage(getDurableStateStorage),
      partialize: (state) => ({
        mappingByScope: state.mappingByScope,
        validationByScope: state.validationByScope,
      }),
      merge: (persistedState, currentState) => {
        if (!isRecord(persistedState)) {
          return currentState;
        }

        return {
          ...currentState,
          mappingByScope: sanitizeMappingByScope(persistedState.mappingByScope),
          validationByScope: sanitizeValidationByScope(persistedState.validationByScope),
        };
      },
    }
  )
);

// The persisted slice is mirrored into the plugin-side durable store. When a
// WebView2 purge wiped localStorage, bootstrap hydration restores the
// mirrored value into localStorage and then asks us to re-read it.
registerDurableRehydrator(() => useWorkflowResumeStore.persist.rehydrate());
